import { html, LitElement, render } from 'lit';
import { html as staticHtml, unsafeStatic } from 'lit/static-html.js';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { GridStack, GridStackWidget } from 'gridstack';
import { styles } from '../view/styles';
import { WEB_COMPONENT_GZ_GRID_STACK_MANAGER } from '../../../webcomponents.constants';
import {
  AlignTitle,
  WidgetEvents,
  WidgetStateBackendToFrontEnd,
  WidgetStateFrontEnd,
} from '../../gzGenericWidget/types';
import { connect } from 'pwa-helpers';
import { RootState, store } from '../../../redux/store';
import {
  markWidgetInactive,
  selectActiveWidgets,
  setRefreshWidgets,
  updateWidget,
  updateWidgetsConfigs,
} from '../../redux/dashboards.widgets.slice';
import { DashboardReduxWidget } from '../../redux/types';
import { isEqual, sortBy } from 'lodash';
import { GridItemHTMLElement } from 'gridstack/dist/types';
import { GridStackHelper } from '../helpers/gridStack.helper';
import { htmlEncode } from '../../../utils/escape.utils';
import { DashboardAppState, selectDashboardsAppState } from '../../redux/dashboards.app.reducer';
import {
  selectActiveViewConfiguration,
  selectGridStackFirstInitOfWidgets,
  selectGridStackReset,
  setResetGridStack,
} from '../../redux/dashboards.smart.views.slice';
import { Layout, ViewType } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { ifDefined } from 'lit/directives/if-defined.js';
import { getTranslatedWidgetTitle } from '../../utils/translations';
import { DashboardSmartView } from '../../gzDashboardSmartViewsList/types';
import { GzGenericWidgetContent } from '../../gzGenericWidget';
import { GridStackEvents } from '../types';
import { DisplayMode } from '../../../types/common/types';

/**
 * @summary Manages widgets with gridstack.
 * @tag gz-dashboard-grid-stack-manager
 */
@customElement(WEB_COMPONENT_GZ_GRID_STACK_MANAGER)
export class GzDashboardGridStackManagerWebComponent extends connect(store)(LitElement) {
  /**
   * @internal
   */
  @query('.grid-stack')
  gridStackElement;

  @property({
    attribute: false,
    reflect: false,
    hasChanged(value: Array<DashboardReduxWidget>, oldValue: Array<DashboardReduxWidget>): boolean {
      return !isEqual(value, oldValue);
    },
  })
  public widgets: Array<DashboardReduxWidget> = [];

  /**
   * @internal
   */
  private gridStack: GridStack;
  @property({ attribute: false, reflect: false })
  private shouldReset: boolean;

  @property({ attribute: false, reflect: false })
  private firstInitOfWidgets: boolean;

  @property({ attribute: false })
  private activeViewConfig: DashboardSmartView;

  @property({ type: Boolean, attribute: true, reflect: true })
  withoutLoadingAnimations? = false;

  @property({ attribute: false })
  private dependencies: any;

  @property({ attribute: true })
  public dashboardType: number;

  @property({ attribute: true })
  public displayMode: DisplayMode;

  @state()
  private isResizingOrMoving = false;

  private gridConfig = {
    hideWidgetActionsOnLoading: false, // controls whether to hide move, resize and cancel action buttons when widget is in loading state
    columnCount: 12,
    // in px
    cellHeight: 56,
    gutter: 16,
    margin: 12, // visual margin is actually 20, increased by half gutter (12 + 16/2)
  };

  static get styles() {
    return styles;
  }

  stateChanged(rootState: RootState) {
    const dashboardsAppState: DashboardAppState = selectDashboardsAppState(rootState);
    if (Number(dashboardsAppState.uiSlice.configuration.dashboardType) !== Number(this.dashboardType)) {
      return;
    }
    this.shouldReset = selectGridStackReset(dashboardsAppState);
    this.firstInitOfWidgets = selectGridStackFirstInitOfWidgets(dashboardsAppState);
    this.widgets = selectActiveWidgets(dashboardsAppState);
    this.activeViewConfig = selectActiveViewConfiguration(dashboardsAppState);
  }

  public injectDependencies(dependencies: any): void {
    this.dependencies = dependencies;
  }

  protected firstUpdated(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.firstUpdated(_changedProperties);
    this.initGridStack();
    this.requestUpdate();
  }

  private initGridStack() {
    const gridOptions = {
      animate: true,
      removable: false,
      column: 12,
      disableResize: false,
      itemClass: 'grid-stack-item',
      disableDrag: true,
      margin: this.gridConfig.gutter / 2,
      cellHeight: this.gridConfig.cellHeight + this.gridConfig.gutter,
      alwaysShowResizeHandle: false,
    };
    this.gridStack = GridStack.init(gridOptions, this.gridStackElement);
    this.gridStack.on('dragstop', this.onStopDrag.bind(this));
    this.gridStack.on('resizestart', this.onStartResize.bind(this));
    this.gridStack.on('resizestop', this.onStopResize.bind(this));
    this.gridStack.on('change', this.onChanged.bind(this));
    this.gridStack.on('added', this.onAdded.bind(this));
  }

  render() {
    const gridWrapperClasses = {
      'grid-wrapper': true,
      'grid-wrapper-show-cells': this.isResizingOrMoving,
    };
    const gridStackClasses = {
      'grid-stack': true,
      'grid-stack-show-cells': this.isResizingOrMoving,
    };
    const styleVariables = `
      --column-count: ${this.gridConfig.columnCount};
      --cell-height: ${this.gridConfig.cellHeight + this.gridConfig.gutter}px;
      --grid-half-gutter: ${this.gridConfig.gutter / 2}px;
      --grid-margin: ${this.gridConfig.margin}px;
    `;
    return html`
      <div class="${classMap(gridWrapperClasses)}" style="${styleVariables}">
        <div class="${classMap(gridStackClasses)}" @change="${this.handleChangeEvent}">${this.renderWidgets()}</div>
      </div>
    `;
  }

  private renderWidgets() {
    const newWidgetsIds = this.widgets.map(widget => widget.id);
    const oldWidgetsIds = this.gridStack?.engine.nodes.map(node => node.id);

    if (this.shouldReset) {
      this.resetGridStack();
    }

    if (!isEqual(sortBy(newWidgetsIds), sortBy(oldWidgetsIds))) {
      // all widgets should be removed and reinitialize them
      this.resetGridStack();
    }
  }

  public renderWidget(id: string, payload?: any) {
    const gridStackWidget = this.gridStack?.engine.nodes.find(widget => widget.id === id);
    const widgetConfig = this.widgets.find(config => config.id === id);
    if (!gridStackWidget || !widgetConfig) {
      return;
    }
    const gridStackResizeButton = gridStackWidget.el?.lastChild as HTMLElement;
    const widget = this.shadowRoot?.querySelector(`#${htmlEncode(id)}`) as GzGenericWidgetContent;

    widget.injectDependencies(this.dependencies);
    // @ts-ignore
    render(this.renderWidgetContent(widgetConfig, gridStackResizeButton, payload), gridStackWidget.el?.firstChild);
  }

  private renderWidgetContent(widget: DashboardReduxWidget, gridStackResizeButton: HTMLElement, payload?: any) {
    // <${config.id}> should exist as a component that extends GzGenericWidgetContent
    // As we use unsafeStatic, the information must be html encoded
    const encodedTagName = htmlEncode(widget.id);
    this.hideResizeButtonOnLoading(
      gridStackResizeButton,
      WidgetStateBackendToFrontEnd.get(payload?.state) ?? WidgetStateFrontEnd.IS_LOADING
    );

    const alignTitle = widget.config.alignTitle ? widget.config.alignTitle : AlignTitle.LEFT;

    return html`
      <gz-generic-widget
        id="${encodedTagName}"
        widgetTitle="${getTranslatedWidgetTitle(encodedTagName)}"
        state="${WidgetStateBackendToFrontEnd.get(payload?.state) ?? WidgetStateFrontEnd.IS_LOADING}"
        ?resizable="${widget.config.resizable}"
        ?movable="${widget.config.movable}"
        ?closable="${widget.config.closable}"
        alignTitle="${alignTitle}"
        ?hideTitle="${widget.config.hideTitle}"
        ?hideActionsOnLoading="${this.gridConfig.hideWidgetActionsOnLoading}"
        ?titleHasAction="${widget.config.titleHasAction}"
        contentOrientation="${ifDefined(widget.config.contentOrientation)}"
        widgetContentStyle="${ifDefined(widget.config.widgetContentStyle)}"
      >
        ${payload?.data
          ? staticHtml`
            <${unsafeStatic(encodedTagName)}
              .payload="${payload?.data}"
              .state="${WidgetStateBackendToFrontEnd.get(payload?.state) ?? WidgetStateFrontEnd.IS_LOADING}"
              .loadingRows="${widget.config.defaultSize.height}"
              .resizeBreakingPoints="${widget.config.resizeBreakingPoints}"
              ?hideActionsOnLoading="${this.gridConfig.hideWidgetActionsOnLoading}"
            />
          `
          : staticHtml`
            <${unsafeStatic(encodedTagName)}
              .state="${WidgetStateBackendToFrontEnd.get(payload?.state) ?? WidgetStateFrontEnd.IS_LOADING}"
              .loadingRows="${widget.config.defaultSize.height}"
              .resizeBreakingPoints="${widget.config.resizeBreakingPoints}"
              ?hideActionsOnLoading="${this.gridConfig.hideWidgetActionsOnLoading}"
            />
          `}
      </gz-generic-widget>
    `;
  }

  private hideResizeButtonOnLoading(gridStackResizeButton: HTMLElement, widgetState: WidgetStateFrontEnd | undefined) {
    if (!gridStackResizeButton || !this.gridConfig.hideWidgetActionsOnLoading) {
      return;
    }
    const hideResizeButtonClass = 'hide-resize-button';
    if (!widgetState || widgetState === WidgetStateFrontEnd.IS_LOADING) {
      gridStackResizeButton.classList.add(hideResizeButtonClass);
    } else {
      gridStackResizeButton.classList.remove(hideResizeButtonClass);
    }
  }

  private resetGridStack() {
    if (!this.gridStack) {
      return;
    }

    this.gridStack.removeAll();
    this.initWidgets();
    // this is needed in order to first load the widget data and also if something make the grid stack to reset
    if (this.shouldReset) {
      store.dispatch(setResetGridStack(false));
    }

    store.dispatch(setRefreshWidgets(true));
  }

  private initWidgets() {
    this.gridStack.batchUpdate(true);
    this.widgets.forEach(widgetConfig => {
      this.addWidget(widgetConfig);
    });
    this.gridStack.batchUpdate(false);
    if (this.activeViewConfig.type === ViewType.DEFAULT) {
      this.gridStack.compact();
    }
  }

  private addWidget(widget: DashboardReduxWidget) {
    const widgetElement = this.gridStack.addWidget(GridStackHelper.transformWidgetConfigToGridConfig(widget));
    const gridStackResizeButton = widgetElement.lastChild as HTMLElement;
    this.addWidgetListeners(widgetElement);
    // @ts-ignore
    render(this.renderWidgetContent(widget, gridStackResizeButton), widgetElement.firstChild);
    const newLayout: Layout = {
      position: {
        x: widgetElement.gridstackNode?.x,
        y: widgetElement.gridstackNode?.y,
      },
      size: {
        width: widgetElement.gridstackNode?.w,
        height: widgetElement.gridstackNode?.h,
      },
    };

    // this should happen only if smart view widgets are not configured well in terms of position
    if (!isEqual(newLayout.position, widget.activeLayout?.position)) {
      store.dispatch(
        updateWidget({
          id: widget.id,
          changes: {
            activeLayout: {
              ...newLayout,
            },
          },
        })
      );
    }
  }

  private addWidgetListeners(widget: HTMLElement) {
    widget.addEventListener(WidgetEvents.REMOVED, this.removeWidget.bind(this));
    widget.addEventListener(WidgetEvents.START_DRAG, this.onStartDrag.bind(this));
  }

  private handleChangeEvent(e: CustomEvent) {
    const changedWidgets = e.detail as GridStackWidget[];
    const changedConfigs = changedWidgets.map(config => {
      const oldConfigs = this.widgets.find(widget => widget.id === config.id);
      if (!oldConfigs) {
        throw new Error('Could not find active widget with id: ' + config.id);
      }
      return {
        id: oldConfigs.id,
        changes: {
          activeLayout: {
            ...GridStackHelper.getPositionAndSizeFromGridStackWidget(config),
          },
        },
      };
    });

    store.dispatch(updateWidgetsConfigs(changedConfigs));
  }

  private removeWidget(e: CustomEvent) {
    const widget = e.currentTarget as GridItemHTMLElement;
    if (!widget || !widget.gridstackNode?.id) {
      throw new Error('Could not find the widget that needs to be removed');
    }
    const id = widget.gridstackNode?.id;
    this.gridStack.removeWidget(widget);
    this.gridStack.compact();
    store.dispatch(markWidgetInactive(id.toString()));
  }

  private onStartDrag() {
    this.gridStack.enableMove(true);
    this.isResizingOrMoving = true;
  }

  private onStopDrag() {
    this.gridStack.enableMove(false);
    this.isResizingOrMoving = false;
  }

  private onStartResize() {
    this.isResizingOrMoving = true;
  }

  private onStopResize() {
    this.isResizingOrMoving = false;
  }

  private onChanged() {
    if (this.activeViewConfig.type === ViewType.CUSTOM) {
      this.dispatchEvent(new CustomEvent(GridStackEvents.GRID_AUTO_SAVED, { bubbles: true, composed: true }));
    }
  }

  private onAdded() {
    if (this.activeViewConfig.type === ViewType.CUSTOM && !this.firstInitOfWidgets) {
      this.dispatchEvent(new CustomEvent(GridStackEvents.GRID_AUTO_SAVED, { bubbles: true, composed: true }));
    }
  }
}
